import { AlertCircle, Lock, Shield, User } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Card } from '../components/ui/card'
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '../components/ui/modal'
import { FormField } from '../components/ui/form-field'
import { Input } from '../components/ui/input'
import { LoadingSpinner } from '../components/ui/loading-spinner'
import { PageHeader } from '../components/ui/page-header'
import { SectionHeader } from '../components/ui/section-header'
import { SettingsRow } from '../components/ui/settings-row'
import { UnifiedButton } from '../components/ui/unified-button'
import { supabase } from '../lib/supabase'
import { useAuth } from '../stores/authStore'

const ProfilePage = () => {
  const { user, signOut } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  // Password change modal state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null)
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)

  const handlePasswordChange = async () => {
    setPasswordChangeError(null)

    // Validate passwords
    if (!currentPassword) {
      setPasswordChangeError('Please enter your current password')
      return
    }

    if (newPassword.length < 8) {
      setPasswordChangeError('New password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordChangeError('New passwords do not match')
      return
    }

    setIsUpdatingPassword(true)

    try {
      // First verify current password by signing in
      if (!user?.email) {
        throw new Error('User email not available')
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      })

      if (signInError) {
        throw new Error('Current password is incorrect')
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (updateError) throw updateError

      // Success - close modal and show success message
      setShowPasswordChangeModal(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      toast.success('Password updated successfully!')
    } catch (err) {
      setPasswordChangeError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setIsUpdatingPassword(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') return

    setIsDeleting(true)
    try {
      // Call the edge function to delete the account
      const { data: _data, error } = await supabase.functions.invoke('delete-account')

      if (error) {
        throw new Error(error.message || 'Failed to delete account')
      }

      // Sign out after successful deletion
      await signOut()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account')
      setIsDeleting(false)
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <PageHeader
        title="Profile"
        description="Manage your account information and security settings"
        icon={User}
      />

      <div className="grid grid-cols-1 gap-2">
        {/* Account Information */}
        <Card className="p-6">
          <SectionHeader title="Account Information" icon={User} />
          <div className="mt-4 space-y-4">
            <SettingsRow
              title="Email Address"
              description="Your primary email for signing in"
              action={
                <div className="text-muted-foreground text-sm">
                  {user?.email || 'Not available'}
                </div>
              }
            />
            <SettingsRow
              title="User ID"
              description="Your unique identifier"
              action={
                <div className="text-muted-foreground font-mono text-xs">
                  {user?.id ? `${user.id}` : 'Not available'}
                </div>
              }
            />
            <SettingsRow
              title="Account Created"
              description="When you joined Loadline"
              action={
                <div className="text-muted-foreground text-sm">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'Unknown'}
                </div>
              }
            />
          </div>
        </Card>

        {/* Security */}
        <Card className="p-6">
          <SectionHeader title="Security" icon={Lock} />
          <div className="mt-4 space-y-4">
            {error && (
              <div className="text-destructive bg-destructive/10 flex items-start gap-2 rounded-md p-3 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <SettingsRow
              title="Password"
              description="Change your account password"
              action={
                <UnifiedButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowPasswordChangeModal(true)}
                >
                  Change Password
                </UnifiedButton>
              }
            />
          </div>
        </Card>

        {/* Account Actions */}
        <Card className="p-6">
          <SectionHeader title="Account Management" icon={Shield} />
          <div className="mt-4 space-y-4">
            <SettingsRow
              title="Sign Out"
              description="Sign out of your Loadline account on this device"
              action={
                <UnifiedButton variant="outline" size="sm" onClick={handleSignOut}>
                  Sign Out
                </UnifiedButton>
              }
            />

            <div className="border-t pt-2">
              <SettingsRow
                title="Delete Account"
                description="Permanently delete your account and all associated data"
                action={
                  <UnifiedButton
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete Account
                  </UnifiedButton>
                }
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Account Dialog */}
      <Modal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Delete Account</ModalTitle>
            <ModalDescription>
              This action cannot be undone. This will permanently delete your account and remove all
              your data from our servers.
            </ModalDescription>
          </ModalHeader>
          <div className="space-y-4 py-4">
            <div className="bg-destructive/10 rounded-md p-4">
              <p className="text-destructive mb-2 text-sm font-medium">
                Warning: This will delete:
              </p>
              <ul className="text-destructive/80 list-inside list-disc space-y-1 text-sm">
                <li>All your workout data</li>
                <li>Weight tracking history</li>
                <li>Settings and preferences</li>
                <li>API keys and integrations</li>
              </ul>
            </div>
            <FormField label='Type "DELETE" to confirm'>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE to confirm"
              />
            </FormField>
          </div>
          <ModalFooter>
            <UnifiedButton
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false)
                setDeleteConfirmation('')
              }}
              disabled={isDeleting}
            >
              Cancel
            </UnifiedButton>
            <UnifiedButton
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== 'DELETE' || isDeleting}
            >
              {isDeleting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Account'
              )}
            </UnifiedButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Password Change Modal */}
      <Modal open={showPasswordChangeModal} onOpenChange={setShowPasswordChangeModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Your Password
            </ModalTitle>
            <ModalDescription>Enter your current password and choose a new one.</ModalDescription>
          </ModalHeader>

          <div className="space-y-4 py-4">
            {passwordChangeError && (
              <div className="text-destructive bg-destructive/10 flex items-start gap-2 rounded-md p-3 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{passwordChangeError}</span>
              </div>
            )}

            <FormField label="Current Password">
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                disabled={isUpdatingPassword}
              />
            </FormField>

            <FormField label="New Password">
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={8}
                disabled={isUpdatingPassword}
              />
            </FormField>

            <FormField label="Confirm New Password">
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                minLength={8}
                disabled={isUpdatingPassword}
              />
            </FormField>
          </div>

          <ModalFooter>
            <UnifiedButton
              variant="outline"
              onClick={() => {
                setShowPasswordChangeModal(false)
                setCurrentPassword('')
                setNewPassword('')
                setConfirmPassword('')
                setPasswordChangeError(null)
              }}
              disabled={isUpdatingPassword}
            >
              Cancel
            </UnifiedButton>
            <UnifiedButton
              onClick={handlePasswordChange}
              disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword}
            >
              {isUpdatingPassword ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </UnifiedButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default ProfilePage
